import React, { useMemo, useState, useRef, useEffect, useContext } from 'react';
import { Box, FormHelperText, IconButton, Tooltip, CircularProgress, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { AutoFixHigh as AIIcon } from '@mui/icons-material';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { useTheme } from '@mui/material/styles';
import { generateProductDescription } from '../services/ai-product-info.service';
import { Product } from '../types/api';
import GlobalMessageContext from '../context/globalMessage.context';

interface WysiwygEditorProps {
  defaultValue?: string;
  onChange?: (html: string) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
  productData?: Partial<Product>;
  showAIButton?: boolean;
}

export const WysiwygEditor: React.FC<WysiwygEditorProps> = ({
  defaultValue = '',
  onChange,
  label,
  error = false,
  helperText,
  placeholder = 'Enter product description...',
  disabled = false,
  'data-testid': testId,
  productData,
  showAIButton = false,
}) => {
  const theme = useTheme();
  const [isGenerating, setIsGenerating] = useState(false);
  const [deepSearch, setDeepSearch] = useState(false);
  const uxMessage = useContext(GlobalMessageContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);

  // Configure Quill with only the HTML tags we need
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  }), []);

  // Initialize Quill instance
  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    quillRef.current = new Quill(containerRef.current, {
      theme: 'snow',
      placeholder: placeholder,
      readOnly: disabled,
      modules: modules,
    });

    // Set initial content only once with defaultValue
    if (defaultValue) {
      quillRef.current.setContents(quillRef.current.clipboard.convert({html: defaultValue }), 'silent');
    }

    // Handle text changes with user source filtering to avoid form interference
    quillRef.current.on('text-change', (_delta, _oldDelta, source) => {
      if (quillRef.current && source === 'user') {
        // Only sync on actual user changes, not programmatic updates
        const content = quillRef.current.root.innerHTML;
        onChange?.(content);
      }
    });
  }, []); // Empty dependency array for uncontrolled mode

  const cleanHtmlContent = (html: string): string => {
    if (!html) return html;

    return html
      // Remove empty <p> tags (including variations with whitespace)
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/<p[^>]*>\s*<\/p>/g, '')
      // Remove <p> tags that only contain whitespace, &nbsp;, or <br>
      .replace(/<p[^>]*>\s*(&nbsp;|<br\s*\/??>)*\s*<\/p>/gi, '')
      // Remove multiple consecutive <br> tags (more than 2)
      .replace(/(<br\s*\/??>\s*){3,}/gi, '<br><br>')
      // Remove excessive whitespace between tags, but preserve list structure
      .replace(/(<\/p>)\s{2,}(<p>)/g, '$1$2') // Clean between paragraphs
      .replace(/(<\/h[1-6]>)\s{2,}(<[^u])/g, '$1$2') // Clean after headers but not before <ul>
      .replace(/(<\/div>)\s{2,}(<div>)/g, '$1$2') // Clean between divs
      // Clean up multiple consecutive empty paragraphs
      .replace(/(<p><\/p>\s*){2,}/g, '')
      // Remove completely empty paragraphs with any attributes
      .replace(/<p[^>]*><\/p>/g, '')
      .trim();
  };


  const handleAIGenerate = async () => {
    if (!productData || isGenerating) return;

    try {
      setIsGenerating(true);

      const response = await generateProductDescription(productData, deepSearch);
      const rawDescription = response.description;
      
      console.log('AI Generated Description:', rawDescription);

      // Clean up the HTML content
      const cleanedDescription = cleanHtmlContent(rawDescription);  
      
      // Update the parent component
      onChange?.(cleanedDescription);
      
      // Update the quill content too
      quillRef.current.setContents(quillRef.current.clipboard.convert({html: cleanedDescription}), 'silent');
    } catch (error: any) {
      console.error('Failed to generate AI description:', error);
      uxMessage.setMessage({
        message: `Failed to generate AI description: ${error?.response?.data?.message || error?.message || 'Unknown error occurred'}`,
        severity: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };


  return (
    <Box>
      {label && (
        <Box 
          component="label"
          sx={{ 
            display: 'block',
            mb: 1,
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.66,
            color: error ? theme.palette.error.main : theme.palette.text.secondary,
            textAlign: 'left',
          }}
        >
          {label}
        </Box>
      )}
      <Box>
        <Box 
          className="wysiwyg-editor-container"
          data-testid={testId}
          sx={{
            '& .ql-container': {
              minHeight: 120,
              fontSize: '1rem',
              fontFamily: theme.typography.fontFamily,
              border: error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
              borderTop: 0,
              borderBottom: showAIButton ? 0 : `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
              borderRadius: showAIButton ? 0 : `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
            },
            '& .ql-toolbar': {
              border: error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              backgroundColor: theme.palette.grey[100],
              textAlign: 'left',
            },
            '& .ql-editor': {
              minHeight: 80,
              padding: theme.spacing(1.5),
              color: theme.palette.text.primary,
              '&.ql-blank::before': {
                color: theme.palette.text.secondary,
                fontStyle: 'normal',
              },
              '& p': {
                margin: '0 0 8px 0',
                '&:last-child': {
                  marginBottom: 0,
                },
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                margin: '16px 0 8px 0',
                '&:first-child': {
                  marginTop: 0,
                },
              },
              '& ul, & ol': {
                paddingLeft: theme.spacing(3),
                margin: '8px 0',
              },
              // Fix double margins when lists follow paragraphs
              '& p + ul, & p + ol': {
                marginTop: '0',
              },
              '& li': {
                marginBottom: '4px',
              },
              '& strong': {
                fontWeight: 600,
              },
              '& em': {
                fontStyle: 'italic',
              },
              '& u': {
                textDecoration: 'underline',
              },
            },
            '& .ql-snow .ql-stroke': {
              stroke: theme.palette.text.secondary,
            },
            '& .ql-snow .ql-fill': {
              fill: theme.palette.text.secondary,
            },
          }}
        >
          <div
            ref={containerRef}
            data-testid={testId ? `${testId}-editor` : undefined}
          />
        </Box>
        {showAIButton && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              padding: theme.spacing(1, 1.5),
              border: error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
              borderTop: `1px solid ${theme.palette.divider}`,
              borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.default,
            }}
          >
            {isGenerating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} thickness={4} />
                <Typography variant="body2" color="text.secondary">
                  {deepSearch 
                    ? 'Please wait - performing deep web search for comprehensive product information...' 
                    : 'Please wait - using AI to generate a suitable product description...'
                  }
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={deepSearch ? "Generate description with AI web search" : "Generate description with AI"}>
                  <IconButton
                    onClick={handleAIGenerate}
                    disabled={disabled || !productData?.name}
                    size="small"
                    sx={{
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <AIIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={deepSearch}
                      onChange={(e) => setDeepSearch(e.target.checked)}
                      size="small"
                      disabled={disabled}
                    />
                  }
                  label={
                    <Tooltip title="Enable web search for more comprehensive product information. Uses more advanced AI model and costs more.">
                      <Typography variant="body2" sx={{ cursor: 'help' }}>
                        Deep Search
                      </Typography>
                    </Tooltip>
                  }
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
      {helperText && (
        <FormHelperText error={error}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default WysiwygEditor;